import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { z } from 'zod';

import { type AudienceType } from '@/api/audience';
import { withFieldGroup } from '@/components/Form';
import { FieldLabel } from '@/components/Form/fields';
import InfoPopover from '@/components/InfoPopover';
import { maxObjectDescriptionLength, maxObjectNameLength } from '@/constants/numbers';

export type AudienceBaseTanStackFormValues = {
  name: string;
  description: string;
};

export const audienceBaseFormFields = {
  name: 'name',
  description: 'description',
} as const;

const AudienceBaseForm = withFieldGroup({
  defaultValues: {
    name: '',
    description: '',
  } satisfies AudienceBaseTanStackFormValues,
  props: {
    audience: undefined as AudienceType | undefined,
  } as { audience: AudienceType | undefined; autoFocus?: boolean },
  render: function Render({ group, audience, autoFocus = false }) {
    const nameFieldSchema = z
      .string()
      .trim()
      .min(1, t`Name is required`);
    const descriptionFieldSchema = z
      .string()
      .max(
        maxObjectDescriptionLength,
        t`Description must be less than ${maxObjectDescriptionLength} characters`,
      );

    const hasActivations = audience?.activations && audience?.activations?.length > 0;

    return (
      <div className="flex flex-col gap-6">
        <group.AppField
          name="name"
          validators={{
            onDynamic: nameFieldSchema,
          }}
        >
          {(field) => (
            <field.Field>
              <div className="flex items-center gap-2">
                <FieldLabel htmlFor="audience-name">
                  <Trans>Name the Audience</Trans>
                </FieldLabel>
                {audience?.nameIsImmutable && (
                  <InfoPopover
                    title={<Trans>Name cannot be changed</Trans>}
                    body={
                      hasActivations ? (
                        <Trans>
                          This audience has been activated and its name is referenced in vendor
                          platforms. Changing the name will prevent it from being updated in vendor
                          platforms. To rename, first deactivate the audience then change the name
                          and reactivate it. Alternatively, you can create a new audience with the
                          new name and activate it instead.
                        </Trans>
                      ) : (
                        <Trans>
                          The audience was recently deactivated from all vendor platforms. However,
                          it takes up to 24 hours for deactivation to be reflected in vendor
                          platforms. Please try renaming again in 24 hours.
                        </Trans>
                      )
                    }
                  />
                )}
              </div>
              <field.TextInput
                id="audience-name"
                autoFocus={autoFocus}
                disabled={audience?.nameIsImmutable}
                maxLength={maxObjectNameLength}
              />
              <field.Error />
            </field.Field>
          )}
        </group.AppField>
        <group.AppField name="description" validators={{ onSubmit: descriptionFieldSchema }}>
          {(field) => (
            <field.Field>
              <FieldLabel htmlFor="audience-description">
                <Trans>Describe the Audience</Trans>
              </FieldLabel>
              <field.TextArea
                id="audience-description"
                maxLength={maxObjectDescriptionLength}
                rows={3}
              />
              <field.Error />
            </field.Field>
          )}
        </group.AppField>
      </div>
    );
  },
});

export default AudienceBaseForm;
